// src/app/components/chat/ChatThread.tsx
//
// Painel principal do Chat: conecta o WebSocket do grupo selecionado,
// carrega o histórico via REST (paginado por cursor) e mescla eventos
// ao vivo. Edição/reação/exclusão preferem o WebSocket (quando conectado)
// para que o "eco" broadcast atualize todo mundo em tempo real — REST é
// usado como fallback (ver comentário em ChatComposer.tsx sobre a
// assimetria: apps.chat.services não faz broadcast sozinho, só o
// consumer WS faz group_send explicitamente após chamar o serviço).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, Users, WifiOff, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { chatService, type ChatGroup, type ChatMessage, type ChatUserMini } from '../../services/chatService';
import { createChatSocket } from '../../lib/chatSocket';
import { ApiError } from '../../services/api';
import { ChatMessageBubble } from './ChatMessageBubble';
import ChatComposer from './ChatComposer';
import ChatMemberPanel from './ChatMemberPanel';
import { formatDaySeparator } from './chatUtils';

export interface ChatThreadProps {
  group: ChatGroup;
  currentUserId: number;
  canDeleteAny: boolean;
  /** Propaga o grupo atualizado (ex: novo avatar_url) pro estado do pai. */
  onGroupUpdated?: (group: ChatGroup) => void;
  /** Chamado quando o usuário logado sai do grupo (kind='group') pelo
   * painel de membros — o pai deve remover o grupo da lista e navegar
   * para outra conversa. */
  onLeftGroup?: (groupId: string) => void;
  /** Versão enxuta pro painel flutuante de suporte (SupportChatLauncher):
   * sem cabeçalho próprio (o painel já tem um), sem busca e sem painel de
   * participantes — só o aviso de conexão, quando houver. */
  compact?: boolean;
  /** Texto do estado vazio — padrão "Nenhuma mensagem ainda. Diga oi 👋". */
  emptyHint?: string;
}

type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'lost';

function upsertMessage(list: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  const idx = list.findIndex((m) => m.id === incoming.id);
  if (idx === -1) return [...list, incoming];
  const next = list.slice();
  next[idx] = incoming;
  return next;
}

/** Mesma lógica de upsertMessage, mas nunca insere — usada para refletir
 * edição/exclusão em `searchResults` (resultado estático da busca, onde
 * uma mensagem NOVA nunca deveria aparecer só porque chegou ao vivo). */
function patchExistingMessage(list: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  const idx = list.findIndex((m) => m.id === incoming.id);
  if (idx === -1) return list;
  const next = list.slice();
  next[idx] = incoming;
  return next;
}

export default function ChatThread({
  group,
  currentUserId,
  canDeleteAny,
  onGroupUpdated,
  onLeftGroup,
  compact = false,
  emptyHint = 'Nenhuma mensagem ainda. Diga oi 👋',
}: ChatThreadProps) {
  const { id: groupId, display_name: groupName } = group;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursorUrl, setNextCursorUrl] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [reconnectAttempt, setReconnectAttempt] = useState<{ attempt: number; max: number } | null>(null);
  const [typingLabel, setTypingLabel] = useState<string | null>(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [participantsById, setParticipantsById] = useState<Map<number, ChatUserMini>>(new Map());
  const [readWatermarks, setReadWatermarks] = useState<Map<number, number>>(new Map());

  // ── Busca no histórico do grupo — GET .../messages/?search=, já suportado
  // pela API (content OU título de card compartilhado) mas até aqui sem
  // nenhum campo na interface pra usá-lo. Modo de busca substitui a lista
  // ao vivo por um resultado estático (sem scroll infinito, sem merge de
  // eventos WS) — "Limpar" volta pra conversa normal.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ChatMessage[] | null>(null);
  const [searching, setSearching] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const socketRef = useRef(createChatSocket());
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isNearBottomRef = useRef(true);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // ── Conexão + histórico inicial ─────────────────────────────────────────
  useEffect(() => {
    const socket = socketRef.current;

    setMessages([]);
    setNextCursorUrl(null);
    setLoadingInitial(true);
    setConnectionState('connecting');
    setReconnectAttempt(null);
    setTypingLabel(null);
    setParticipantsById(new Map());
    setReadWatermarks(new Map());
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
    isNearBottomRef.current = true;

    const onOpen = () => {
      setConnectionState('connected');
      setReconnectAttempt(null);
    };
    const onReconnecting = (payload: { attempt: number; max: number }) => {
      setConnectionState('reconnecting');
      setReconnectAttempt({ attempt: payload.attempt, max: payload.max });
    };
    const onConnectionLost = () => setConnectionState('lost');

    const onMessageNew = (payload: unknown) => {
      const msg = payload as ChatMessage;
      setMessages((prev) => upsertMessage(prev, msg));
      if (isNearBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom('smooth'));
      }
      chatService.markRead(groupId, msg.id).catch(() => {});
    };

    const onMessageUpdated = (payload: unknown) => {
      const msg = payload as ChatMessage;
      setMessages((prev) => upsertMessage(prev, msg));
      setSearchResults((prev) => (prev ? patchExistingMessage(prev, msg) : prev));
    };

    const onMessageDeleted = (payload: unknown) => {
      const msg = payload as ChatMessage;
      setMessages((prev) => upsertMessage(prev, msg));
      setSearchResults((prev) => (prev ? patchExistingMessage(prev, msg) : prev));
    };

    const onReactionToggled = (payload: { message_id: string; emoji: string; user_id: number; added: boolean }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== payload.message_id) return m;
          const reactions = m.reactions.slice();
          const idx = reactions.findIndex((r) => r.emoji === payload.emoji);

          if (payload.added) {
            if (idx === -1) {
              reactions.push({ emoji: payload.emoji, count: 1, user_ids: [payload.user_id] });
            } else if (!reactions[idx].user_ids.includes(payload.user_id)) {
              reactions[idx] = {
                ...reactions[idx],
                count: reactions[idx].count + 1,
                user_ids: [...reactions[idx].user_ids, payload.user_id],
              };
            }
          } else if (idx !== -1) {
            const userIds = reactions[idx].user_ids.filter((id) => id !== payload.user_id);
            if (userIds.length === 0) {
              reactions.splice(idx, 1);
            } else {
              reactions[idx] = { ...reactions[idx], count: userIds.length, user_ids: userIds };
            }
          }

          return { ...m, reactions };
        }),
      );
    };

    const onReadUpdated = (payload: { user_id: number; message_id: string; sequence: number }) => {
      setReadWatermarks((prev) => {
        const current = prev.get(payload.user_id) ?? 0;
        if (payload.sequence <= current) return prev;
        const next = new Map(prev);
        next.set(payload.user_id, payload.sequence);
        return next;
      });
    };

    const onTyping = (payload: { user_id: number; username: string }) => {
      if (payload.user_id === currentUserId) return;
      setTypingLabel(`${payload.username} está digitando...`);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setTypingLabel(null), 3000);
    };

    const onError = (payload: { message: string }) => {
      toast.error(payload.message || 'Erro no chat.');
    };

    socket.on('open', onOpen);
    socket.on('reconnecting', onReconnecting);
    socket.on('connection-lost', onConnectionLost);
    socket.on('chat.message.new', onMessageNew);
    socket.on('chat.message.updated', onMessageUpdated);
    socket.on('chat.message.deleted', onMessageDeleted);
    socket.on('chat.reaction.toggled', onReactionToggled);
    socket.on('chat.read.updated', onReadUpdated);
    socket.on('chat.typing', onTyping);
    socket.on('error', onError);

    socket.connect(groupId);

    chatService
      .getGroupMembers(groupId)
      .then((members) => {
        const byId = new Map<number, ChatUserMini>();
        const watermarks = new Map<number, number>();
        members.forEach((m) => {
          byId.set(m.user.id, m.user);
          watermarks.set(m.user.id, m.last_read_sequence);
        });
        setParticipantsById(byId);
        setReadWatermarks(watermarks);
      })
      .catch(() => {
        // erro silencioso — "visto por" só não aparece neste grupo
      });

    chatService
      .getMessages(groupId)
      .then((page) => {
        const ordered = page.results.slice().reverse();
        setMessages(ordered);
        setNextCursorUrl(page.next);
        if (ordered.length > 0) {
          chatService.markRead(groupId, ordered[ordered.length - 1].id).catch(() => {});
        }
        requestAnimationFrame(() => scrollToBottom('auto'));
      })
      .catch(() => {
        toast.error('Não foi possível carregar o histórico do chat.');
      })
      .finally(() => setLoadingInitial(false));

    return () => {
      socket.off('open', onOpen);
      socket.off('reconnecting', onReconnecting);
      socket.off('connection-lost', onConnectionLost);
      socket.off('chat.message.new', onMessageNew);
      socket.off('chat.message.updated', onMessageUpdated);
      socket.off('chat.message.deleted', onMessageDeleted);
      socket.off('chat.reaction.toggled', onReactionToggled);
      socket.off('chat.read.updated', onReadUpdated);
      socket.off('chat.typing', onTyping);
      socket.off('error', onError);
      socket.disconnect();
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // ── Busca — debounce de 400ms, cancela requisição obsoleta via flag local ──
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);

    const query = searchQuery.trim();
    if (!query) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    searchDebounceRef.current = setTimeout(() => {
      chatService
        .getMessages(groupId, { search: query })
        .then((page) => {
          if (cancelled) return;
          setSearchResults(page.results);
        })
        .catch(() => {
          if (cancelled) return;
          toast.error('Não foi possível buscar mensagens.');
          setSearchResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 400);

    return () => {
      cancelled = true;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery, groupId]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
  };

  const isSearching = searchResults !== null;
  const displayedMessages = searchResults ?? messages;

  // ── Scroll infinito para cima (mensagens antigas) — desligado em modo de
  // busca, onde a lista é um resultado estático, não a conversa ao vivo. ──
  const loadMore = useCallback(() => {
    if (isSearching || !nextCursorUrl || loadingMore) return;
    setLoadingMore(true);
    prevScrollHeightRef.current = scrollRef.current?.scrollHeight ?? null;

    chatService
      .getMessages(groupId, { cursorUrl: nextCursorUrl })
      .then((page) => {
        const older = page.results.slice().reverse();
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id));
          return [...older.filter((m) => !existingIds.has(m.id)), ...prev];
        });
        setNextCursorUrl(page.next);
      })
      .catch(() => {
        toast.error('Não foi possível carregar mensagens antigas.');
      })
      .finally(() => setLoadingMore(false));
  }, [groupId, nextCursorUrl, loadingMore, isSearching]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || prevScrollHeightRef.current === null) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    prevScrollHeightRef.current = null;
  }, [messages]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;

    if (el.scrollTop < 80) loadMore();
  };

  // ── Ações de mensagem — WS quando conectado, REST como fallback ────────
  const socketConnected = connectionState === 'connected';

  const handleEdit = (messageId: string, content: string) => {
    if (socketConnected) {
      socketRef.current.send({ type: 'message.edit', message_id: messageId, content });
      return;
    }
    chatService
      .editMessage(messageId, content)
      .then((updated) => {
        setMessages((prev) => upsertMessage(prev, updated));
        setSearchResults((prev) => (prev ? patchExistingMessage(prev, updated) : prev));
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Erro ao editar mensagem.'));
  };

  const handleDelete = (messageId: string) => {
    if (socketConnected) {
      socketRef.current.send({ type: 'message.delete', message_id: messageId });
      return;
    }
    chatService
      .deleteMessage(messageId)
      .then(() => {
        const markDeleted = (list: ChatMessage[]) =>
          list.map((m) => (m.id === messageId ? { ...m, is_deleted: true, content: '[mensagem apagada]' } : m));
        setMessages(markDeleted);
        setSearchResults((prev) => (prev ? markDeleted(prev) : prev));
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Erro ao apagar mensagem.'));
  };

  const handleToggleReaction = (messageId: string, emoji: string) => {
    if (socketConnected) {
      socketRef.current.send({ type: 'reaction.toggle', message_id: messageId, emoji });
      return;
    }
    chatService
      .toggleReaction(messageId, emoji)
      .then((res) => setMessages((prev) => upsertMessage(prev, res.message)))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Erro ao reagir.'));
  };

  const handleMessageSent = (message: ChatMessage) => {
    // Caminho REST (fallback sem socket, ou envio com anexo) — o servidor
    // não faz broadcast de mensagens criadas fora do consumer WS, então
    // precisamos inserir localmente aqui.
    setMessages((prev) => upsertMessage(prev, message));
    requestAnimationFrame(() => scrollToBottom('smooth'));
  };

  // Pra cada leitor, encontra a mensagem MINHA mais recente que ele já leu
  // (sequence <= watermark) e agrupa aí — evita mostrar "visto por" em toda
  // mensagem, só na última que cada pessoa efetivamente alcançou.
  const readByMessageId = useMemo(() => {
    const ownMessages = messages.filter((m) => m.author?.id === currentUserId);
    const result = new Map<string, ChatUserMini[]>();

    readWatermarks.forEach((watermark, userId) => {
      if (userId === currentUserId) return;
      const reader = participantsById.get(userId);
      if (!reader) return;

      let target: ChatMessage | null = null;
      for (const m of ownMessages) {
        if (m.sequence <= watermark) target = m;
        else break;
      }
      if (!target) return;

      const bucket = result.get(target.id) ?? [];
      bucket.push(reader);
      result.set(target.id, bucket);
    });

    return result;
  }, [messages, readWatermarks, participantsById, currentUserId]);

  let lastDay = '';

  return (
    <div className="flex flex-1 min-w-0">
      <div className="flex flex-col flex-1 min-w-0">
        {compact ? (
          connectionState === 'reconnecting' || connectionState === 'lost' ? (
            <p
              className={`px-4 py-1.5 text-[11px] border-b border-slate-200 dark:border-white/[0.08] flex items-center gap-1 ${
                connectionState === 'lost' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'
              }`}
            >
              {connectionState === 'lost' ? (
                <>
                  <WifiOff className="size-3" /> Conexão em tempo real perdida — usando modo offline
                </>
              ) : (
                'Reconectando...'
              )}
            </p>
          ) : null
        ) : (
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0a0b0d]">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{groupName}</h2>
              {connectionState === 'reconnecting' && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  Reconectando...
                  {reconnectAttempt && ` (tentativa ${reconnectAttempt.attempt} de ${reconnectAttempt.max})`}
                </p>
              )}
              {connectionState === 'lost' && (
                <p className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1">
                  <WifiOff className="size-3" /> Conexão em tempo real perdida — usando modo offline
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="icon"
                onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
                title="Buscar nesta conversa"
              >
                <Search className="size-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => setMembersOpen((v) => !v)} title="Participantes">
                <Users className="size-4" />
              </Button>
            </div>
          </div>
        )}

        {searchOpen && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 dark:border-white/[0.08] bg-slate-50 dark:bg-white/[0.03]">
            <Search className="size-3.5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            <input
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar mensagens ou cards compartilhados..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500 dark:text-slate-100"
            />
            {searching && <Loader2 className="size-3.5 animate-spin text-slate-400 dark:text-slate-500 flex-shrink-0" />}
            <button
              type="button"
              onClick={closeSearch}
              className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0"
              title="Limpar busca"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto py-2">
          {loadingInitial ? (
            <div className="p-4 space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-2.5">
                  <Skeleton className="size-8 rounded-full flex-shrink-0 dark:bg-white/10" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3 w-32 dark:bg-white/10" />
                    <Skeleton className="h-4 w-2/3 dark:bg-white/10" />
                  </div>
                </div>
              ))}
            </div>
          ) : searching && searchResults === null ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500 py-16">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : isSearching && displayedMessages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500 py-16">
              <p className="text-sm">Nenhuma mensagem encontrada para "{searchQuery.trim()}".</p>
            </div>
          ) : displayedMessages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-400 dark:text-slate-500 py-16">
              <p className="text-sm text-center px-6">{emptyHint}</p>
            </div>
          ) : (
            <>
              {loadingMore && (
                <div className="flex justify-center py-2">
                  <Loader2 className="size-4 animate-spin text-slate-400 dark:text-slate-500" />
                </div>
              )}
              {displayedMessages.map((message) => {
                const day = formatDaySeparator(message.created_at);
                const showSeparator = day !== lastDay;
                lastDay = day;
                return (
                  <div key={message.id}>
                    {showSeparator && (
                      <div className="flex items-center gap-3 px-4 py-2">
                        <div className="flex-1 h-px bg-slate-200 dark:bg-white/[0.08]" />
                        <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">{day}</span>
                        <div className="flex-1 h-px bg-slate-200 dark:bg-white/[0.08]" />
                      </div>
                    )}
                    <ChatMessageBubble
                      message={message}
                      currentUserId={currentUserId}
                      canDeleteAny={canDeleteAny}
                      readBy={readByMessageId.get(message.id)}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onToggleReaction={handleToggleReaction}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>

        {typingLabel && <p className="px-4 py-1 text-[11px] text-slate-400 dark:text-slate-500 italic">{typingLabel}</p>}

        <ChatComposer
          groupId={groupId}
          socket={socketRef.current}
          socketConnected={socketConnected}
          onMessageSent={handleMessageSent}
          placeholder={compact ? 'Escreva sua mensagem...' : undefined}
        />
      </div>

      {!compact && (
        <ChatMemberPanel
          group={group}
          open={membersOpen}
          currentUserId={currentUserId}
          isPrivileged={canDeleteAny}
          onClose={() => setMembersOpen(false)}
          onGroupUpdated={onGroupUpdated}
          onLeft={() => onLeftGroup?.(group.id)}
        />
      )}
    </div>
  );
}
